import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Box,
  Flex,
  Text,
  DataTable,
  BulkActionBar,
  Button,
  Switch,
  PageHeader,
  useToast,
  TagBadge,
} from '@chatsdet/ui';
import { Buildings, Trash, ArrowsClockwise, Prohibit, Check, CreditCard } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import type { ColumnDef, RowSelectionState, StockFeatures } from '@tanstack/react-table';
import { superadminOrganizationsRoute } from '../../../router';
import {
  useOrganizationsQuery,
  useJoinOrganizationMutation,
  useLeaveOrganizationMutation,
  useBulkJoinOrganizationsMutation,
  type OrganizationWithStats,
} from './useSuperadminData';
import {
  OrganizationAiConfigDialog,
  OrganizationAiProviderCell,
} from './OrganizationAiConfigDialog';
import {
  DeleteOrganizationDialog,
  ToggleOrgStatusDialog,
} from './OrganizationManagementDialogs';
import { OrgBillingDialog } from './OrgBillingDialog';
import { formatTrialRemaining } from '../../../hooks/billingMeters';

export function SuperadminOrganizations() {
  const searchParams = superadminOrganizationsRoute.useSearch();
  const navigate = useNavigate();

  const page = searchParams.page ?? 1;
  const pageSize = searchParams.pageSize ?? 20;
  const search = searchParams.search ?? '';
  const sortBy = searchParams.sortBy;
  const sortOrder = (searchParams.sortOrder as 'asc' | 'desc') ?? undefined;

  const organizationsQuery = useOrganizationsQuery({
    page,
    pageSize,
    search: search || undefined,
    sortBy,
    sortOrder,
  });
  const { toast } = useToast();
  const { switchOrg } = useAuth();
  const joinMutation = useJoinOrganizationMutation();
  const leaveMutation = useLeaveOrganizationMutation();
  const bulkJoinMutation = useBulkJoinOrganizationsMutation();

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [deleteTarget, setDeleteTarget] = React.useState<OrganizationWithStats | null>(null);
  const [statusTarget, setStatusTarget] = React.useState<OrganizationWithStats | null>(null);
  const [billingTarget, setBillingTarget] = React.useState<OrganizationWithStats | null>(null);
  const [aiConfigTarget, setAiConfigTarget] = React.useState<OrganizationWithStats | null>(null);

  const items = organizationsQuery.data?.items ?? [];
  const pagination = organizationsQuery.data?.pagination;
  const totalItems = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const selectedCount = Object.keys(rowSelection).filter((k) => rowSelection[k]).length;

  const updateSearch = React.useCallback(
    (updates: Record<string, unknown>) => {
      navigate({
        to: '/superadmin/organizations',
        search: (prev: Record<string, unknown>) => ({ ...prev, ...updates }),
      });
    },
    [navigate]
  );

  const handleSearchChange = React.useCallback(
    (value: string) => {
      updateSearch({ search: value || undefined, page: 1 });
    },
    [updateSearch]
  );

  const handlePageChange = React.useCallback(
    (p: number) => updateSearch({ page: p }),
    [updateSearch]
  );

  const handlePageSizeChange = React.useCallback(
    (ps: number) => updateSearch({ pageSize: ps, page: 1 }),
    [updateSearch]
  );

  const handleSortChange = React.useCallback(
    (sb: string | undefined, so: 'asc' | 'desc' | undefined) =>
      updateSearch({ sortBy: sb, sortOrder: so }),
    [updateSearch]
  );

  const handleBulkJoin = () => {
    const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);
    const orgIds = selectedIds.map((idx) => items[Number(idx)]?.id).filter(Boolean) as string[];
    if (orgIds.length > 0) {
      bulkJoinMutation.mutate(orgIds, {
        onSuccess: () => {
          setRowSelection({});
          toast.success(`Joined ${orgIds.length} organization(s)`);
        },
        onError: (err) => toast.error(err.message),
      });
    }
  };

  const handleMembershipToggle = React.useCallback(
    (org: OrganizationWithStats) => {
      if (org.isMember) {
        leaveMutation.mutate(org.id, {
          onSuccess: () => toast.success(`Left ${org.name}`),
          onError: (err) => toast.error(err.message),
        });
      } else {
        joinMutation.mutate(org.id, {
          onSuccess: () => toast.success(`Joined ${org.name}`),
          onError: (err) => toast.error(err.message),
        });
      }
    },
    [joinMutation, leaveMutation, toast]
  );

  const columns: ColumnDef<StockFeatures, OrganizationWithStats>[] = React.useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Organization Name',
        cell: ({ row }) => (
          <Flex align="center" gap="sm">
            <Text weight="medium">{row.original.name}</Text>
            <Button
              variant="icon"
              size="sm"
              iconOnly
              tooltip={
                row.original.status === 'inactive'
                  ? 'Disabled organizations cannot be selected'
                  : `Switch to ${row.original.name}`
              }
              isDisabled={row.original.status === 'inactive'}
              onPress={() => switchOrg(row.original.id, '/')}
            >
              <ArrowsClockwise size={14} />
            </Button>
          </Flex>
        ),
        enableSorting: true,
        meta: { flex: 3 },
      },
      {
        accessorKey: 'memberCount',
        header: 'Members',
        cell: ({ row }) => <Text>{row.original.memberCount}</Text>,
        enableSorting: true,
        meta: { flex: 0.5 },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <Text className="text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleDateString()}
          </Text>
        ),
        enableSorting: true,
        meta: { flex: 0.8 },
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) =>
          row.original.status === 'inactive' ? (
            <TagBadge name="Disabled" color="red" hideDot />
          ) : (
            <TagBadge name="Active" color="blue" hideDot />
          ),
        enableSorting: false,
        meta: { flex: 0.5 },
      },
      {
        id: 'trial',
        header: 'Trial',
        cell: ({ row }) => {
          // A contract org's trialEndsAt is history, not a live trial.
          const ends = row.original.billingMode === 'self_serve' ? row.original.trialEndsAt : null;
          if (!ends) return <Text className="text-muted-foreground">—</Text>;
          const { text, expired } = formatTrialRemaining(new Date(ends), new Date());
          return expired ? (
            <TagBadge name={`Expired ${text}`} color="red" hideDot />
          ) : (
            <TagBadge name={`${text} left`} color="orange" hideDot />
          );
        },
        enableSorting: false,
        meta: { flex: 0.6 },
      },
      {
        id: 'providerResolution',
        header: 'AI Provider',
        cell: ({ row }) => (
          <OrganizationAiProviderCell
            org={row.original}
            onEdit={() => setAiConfigTarget(row.original)}
          />
        ),
        enableSorting: false,
        meta: { flex: 1.4 },
      },
      {
        id: 'membership',
        header: 'Member',
        cell: ({ row }) => (
          <Switch
            size="sm"
            isSelected={row.original.isMember ?? false}
            onChange={() => handleMembershipToggle(row.original)}
            isDisabled={joinMutation.isPending || leaveMutation.isPending}
          />
        ),
        meta: { flex: 0.5 },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Flex gap="xs">
            <Button
              variant="ghost"
              size="sm"
              tooltip="Billing"
              onPress={() => setBillingTarget(row.original)}
              className="text-muted-foreground hover:text-foreground"
            >
              <CreditCard size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              tooltip={
                row.original.status === 'inactive'
                  ? 'Re-enable organization'
                  : 'Disable organization'
              }
              onPress={() => setStatusTarget(row.original)}
              className={
                row.original.status === 'inactive'
                  ? 'text-muted-foreground hover:text-success'
                  : 'text-muted-foreground hover:text-destructive'
              }
            >
              {row.original.status === 'inactive' ? <Check size={16} /> : <Prohibit size={16} />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onPress={() => setDeleteTarget(row.original)}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash size={16} />
            </Button>
          </Flex>
        ),
        meta: { flex: 0.5 },
      },
    ],
    [joinMutation.isPending, leaveMutation.isPending, handleMembershipToggle]
  );

  return (
    <Box className="space-y-2">
      <PageHeader
        icon={<Buildings size={24} />}
        title="Organizations"
        description="All organizations in the system"
      />
      <DataTable
        className="[&_th:first-child]:pl-3 [&_td:first-child]:pl-3"
        columns={columns}
        data={items}
        searchable
        searchPlaceholder="Search organizations..."
        searchValue={search}
        onSearchChange={handleSearchChange}
        getRowId={(row) => row.id}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        serverPagination={{
          page,
          pageSize,
          pageCount: totalPages,
          totalItems,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [20, 50, 100],
          isLoading: organizationsQuery.isLoading,
        }}
        serverSorting={{
          sortBy,
          sortOrder,
          onSortChange: handleSortChange,
        }}
        toolbarContent={
          selectedCount > 0 ? (
            <BulkActionBar
              selectedCount={selectedCount}
              itemLabel="organization"
              onClearSelection={() => setRowSelection({})}
              actions={[
                {
                  label: 'Join Selected',
                  onPress: handleBulkJoin,
                  isLoading: bulkJoinMutation.isPending,
                },
              ]}
            />
          ) : undefined
        }
      />

      <DeleteOrganizationDialog org={deleteTarget} onClose={() => setDeleteTarget(null)} />

      <ToggleOrgStatusDialog org={statusTarget} onClose={() => setStatusTarget(null)} />

      <OrgBillingDialog org={billingTarget} onClose={() => setBillingTarget(null)} />

      <OrganizationAiConfigDialog org={aiConfigTarget} onClose={() => setAiConfigTarget(null)} />
    </Box>
  );
}
